import { Box, MenuItem, Modal, TextField, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { tokens } from '../../theme';
import { useTranslation } from '../../hooks/useTranslation';
import { formatCep } from '../../utils';
import { toast } from 'react-toastify';
import { Button } from '../../components/Button';
import api from '../../services/api';
import { HOST_API } from '../../config/hosts';
import { isValidCpfCnpj } from '../customers/form';

const states = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
];

const ModalAddress = ({ open, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [loadingCep, setLoadingCep] = useState(false);

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxWidth: 900,
    bgcolor: '#fff',
    border: `2px solid ${colors.greenAccent[500]}`,
    boxShadow: `0 8px 32px rgba(0,0,0,0.3)`,
    borderRadius: 2,
    outline: 'none',
    p: 4,
  };

  const initialValues = {
    taxvar: '',
    cep: '',
    state: '',
    city: '',
    address: '',
    number: '',
    neighborhood: '',
    complement: '',
  };

  const schema = Yup.object().shape({
    taxvar: Yup.string()
      .required(t('messages.cpfCnpjRequired'))
      .test('cpf-cnpj-valid', t('forms.invalidCpfCnpj'), (value) => isValidCpfCnpj(value)),
    cep: Yup.string().required(t('messages.cepRequired')),
    state: Yup.string().required(t('messages.stateRequired')),
    city: Yup.string().required(t('messages.cityRequired')),
    address: Yup.string().required(t('messages.addressRequired')),
    number: Yup.string().required(t('messages.numberRequired')),
  });

  const fetchAddressByCep = async (cep, setFieldValue) => {
    const cleanedCep = cep.replace(/\D/g, '');

    if (cleanedCep.length !== 8) return;

    setLoadingCep(true);

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanedCep}/json/`);
      const data = await response.json();

      if (data.erro) {
        toast.error(t('messages.zipCodeNotFound'));
        return;
      }

      setFieldValue('address', data.logradouro || '');
      setFieldValue('state', data.uf || '');
      setFieldValue('city', data.localidade || '');
      setFieldValue('neighborhood', data.bairro || '');
    } catch (error) {
      console.error(error);
      toast.error(t('messages.errorFetchingData'));
    } finally {
      setLoadingCep(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleFormSubmit = async (values) => {
    try {
      await api.post(`${HOST_API}/v1/company/address`, values);
      toast.success(t('messages.addressSaved'));
    } catch (error) {
      toast.error(t('messages.errorSaveAddress'));
    } finally {
      handleClose();
    }
  };

  return (
    <Formik
      key={open}
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={handleFormSubmit}
      enableReinitialize
    >
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
        <Modal open={open} onClose={handleClose}>
          <Box sx={modalStyle}>
            <form onSubmit={handleSubmit}>
              <Typography variant="h5" fontWeight="600">
                {t('forms.completeData')}
              </Typography>

              <Typography variant="body2" color="text.secondary" mb={3}>
                {t('forms.subtitleCompleteData')}
              </Typography>

              <Box display="grid" gridTemplateColumns="1fr" gap={2} mb={2}>
                <TextField
                  label={t('forms.cpfCnpj')}
                  name="taxvar"
                  value={values.taxvar}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.taxvar && Boolean(errors.taxvar)}
                  helperText={touched.taxvar && errors.taxvar}
                  fullWidth
                  size="small"
                />
              </Box>

              <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2} mb={2}>
                <TextField
                  label={t('forms.zipCode')}
                  name="cep"
                  value={formatCep(values.cep)}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/\D/g, '');
                    setFieldValue('cep', cleaned);
                  }}
                  onBlur={(e) => {
                    handleBlur(e);
                    const cleaned = e.target.value.replace(/\D/g, '');
                    if (cleaned.length === 8) {
                      fetchAddressByCep(cleaned, setFieldValue);
                    }
                  }}
                  error={touched.cep && Boolean(errors.cep)}
                  helperText={touched.cep && errors.cep}
                  fullWidth
                  inputProps={{ maxLength: 9 }}
                  disabled={loadingCep}
                  size="small"
                />

                <TextField
                  select
                  label={t('forms.state')}
                  name="state"
                  value={values.state}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.state && Boolean(errors.state)}
                  helperText={touched.state && errors.state}
                  fullWidth
                  size="small"
                  disabled={loadingCep}
                >
                  {states.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label={t('forms.city')}
                  name="city"
                  value={values.city}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.city && Boolean(errors.city)}
                  helperText={touched.city && errors.city}
                  fullWidth
                  size="small"
                  disabled={loadingCep}
                />
              </Box>

              <Box display="grid" gridTemplateColumns="2fr 1fr" gap={2} mb={2}>
                <TextField
                  label={t('forms.fullAddress')}
                  name="address"
                  value={values.address}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.address && Boolean(errors.address)}
                  helperText={touched.address && errors.address}
                  fullWidth
                  size="small"
                  disabled={loadingCep}
                />

                <TextField
                  label={t('forms.number')}
                  name="number"
                  value={values.number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.number && Boolean(errors.number)}
                  helperText={touched.number && errors.number}
                  fullWidth
                  size="small"
                />
              </Box>

              {/* Bairro e Complemento */}
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={3}>
                <TextField
                  label={t('forms.neighborhood')}
                  name="neighborhood"
                  value={values.neighborhood}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  size="small"
                />

                <TextField
                  label={t('forms.complement')}
                  name="complement"
                  value={values.complement}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  size="small"
                />
              </Box>

              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button fullWidth={false} onClick={handleClose} variant="outlined">
                  {t('common.cancel')}
                </Button>

                <Button fullWidth={false} type="submit">
                  {t('common.save')}
                </Button>
              </Box>
            </form>
          </Box>
        </Modal>
      )}
    </Formik>
  );
};

export default ModalAddress;
